import {
  NotificationFragment,
  useNotificationsQuery,
} from "@graphql/notifications/Notifications.g";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { NotificationTypeMappingText } from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

import { useNotification } from "@providers/NotificationProvider/useNotification";

const PAGE_SIZE = 30;

const columnHelper = createColumnHelper<NotificationFragment>();

type Props = {
  initialPageIndex?: number;
  clientId?: string;
  notificationType?: NotificationEnum;
  applicationId?: string;
  applicationNameSearch?: string;
};

export const NotificationsTable = ({ 
  initialPageIndex = 0,
  clientId,
  notificationType,
  applicationId,
  applicationNameSearch,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  
  // Build the query variables with filters
  const queryVariables = useMemo(() => {
    const variables: Parameters<typeof useNotificationsQuery>[0] = {
      first: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      resolvedAt_Isnull: true,
      sortBy:
        sorting.length > 0
          ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
          : undefined,
    };

    if (clientId) {
      variables.clientId = clientId;
    }
    
    if (notificationType) {
      variables.notificationType = notificationType;
    }
    
    if (applicationId) {
      variables.applicationId = applicationId;
    }
    
    return variables;
  }, [pagination.pageSize, pagination.pageIndex, sorting, clientId, notificationType, applicationId]);

  const notificationsQuery = useNotificationsQuery(queryVariables);

  const { resolveNotification } = useNotification();
  const navigate = useNavigate();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );


  const totalCount =
    notificationsQuery.data?.notifications?.totalCount ?? undefined;

  const data = useMemo(() => {
    let filteredData = (
      notificationsQuery.data?.notifications?.edges
        .map((edge) => edge?.node)
        .filter((user) => !!user) ?? []
    );

    // Apply client-side filtering for application name search
    if (applicationNameSearch && applicationNameSearch.trim()) {
      const searchTerm = applicationNameSearch.trim().toLowerCase();
      filteredData = filteredData.filter(notification => 
        notification?.application?.resource?.productName
          ?.toLowerCase()
          .includes(searchTerm)
      );
    }

    return filteredData;
  }, [notificationsQuery.data, applicationNameSearch]);

  const handleDetailsClick = useCallback(
    (clientId: string, applicationId: string) => {
      navigate(
        routes.portal.clients.client.applications.application.$buildPath({
          params: { clientId, applicationId },
        }),
        {
          viewTransition: true,
        },
      );
    },
    [navigate],
  );

  const handleResolveClick = useCallback(
    async (id: string) => {
      await resolveNotification(id);
    },
    [resolveNotification],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("client.name", {
        header: "Tenant",
        id: "client",
        cell: (props) => {
          return (
            <Clickable
              onClick={() =>
                handleDetailsClick(
                  props.row.original.client?.id || "",
                  props.row.original.application?.id || "",
                )
              }
              outline="md"
              stretch
            >
              <Table.CompanyCell
                name={props.row.original.client?.name || ""}
                skipLogo={true}
              />
            </Clickable>
          );
        },
        footer: undefined,
      }),
      columnHelper.accessor("application.resource.productName", {
        header: "Application",
        id: "resource__product_name",
        cell: (props) => {
          return (
            <Clickable
              onClick={() =>
                handleDetailsClick(
                  props.row.original.client?.id || "",
                  props.row.original.application?.id || "",
                )
              }
              outline="sm"
              stretch
            >
              <Table.CompanyCell
                name={
                  props.row.original.application?.resource.productName ||
                  "Unknown Notification"
                }
                signedLogoUrl={
                  props.row.original.application?.resource.publicResource
                    ?.signedLogoUrl || undefined
                }
              />
            </Clickable>
          );
        },
        footer: undefined,
      }),
      columnHelper.accessor("notificationType", {
        header: "Type",
        id: "notification_type",
        cell: (props) => {
          const value = props.getValue();
          if (value === null || value === undefined) {
            return null;
          }
          return props.getValue() ? (
            <Clickable
              onClick={() =>
                handleDetailsClick(
                  props.row.original.client?.id || "",
                  props.row.original.application?.id || "",
                )
              }
              outline="sm"
              stretch
            >
              <NotificationTypeMappingText notificationType={value} />
            </Clickable>
          ) : null;
        },
        footer: undefined,
      }),
      columnHelper.accessor("createdAt", {
        header: "Created",
        id: "created_at",
        cell: (props) => {
          return <Table.DateCell date={new Date(props.getValue())} />;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Details"
                  icon="faFileLines"
                  onSelect={() =>
                    handleDetailsClick(
                      props.row.original.client?.id || "",
                      props.row.original.application?.id || "",
                    )
                  }
                />
                <Dropdown.Option
                  label="Resolve"
                  icon="faCheck"
                  onSelect={() => handleResolveClick(props.row.original.id)}
                />
              </Dropdown>
            </Box>
          );
        },
      }),
    ] as ColumnDef<NotificationFragment>[],
    [handleDetailsClick, handleResolveClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table
      columns={columns}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={notificationsQuery.isLoading}
      isFetching={notificationsQuery.isFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
    />
  );
};
